import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { useDefaultSender } from '@/hooks/useDefaultSender';
import { useSendCampaign } from '@/hooks/useCampaigns';
import { useCampaignTemplates } from '@/hooks/useCampaignTemplates';
import { Campaign } from '@/services/campaignService';
import { Send, Loader2, Info, AlertTriangle, CalendarIcon, Clock, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: Campaign;
}

const CampaignSendDialog: React.FC<Props> = ({ open, onOpenChange, campaign }) => {
  const { profile } = useAuth();
  const { defaultSender } = useDefaultSender();
  const sendMutation = useSendCampaign();
  const previewMutation = useSendCampaign();
  const { data: templates = [] } = useCampaignTemplates(profile?.company_id || undefined);

  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [senderSource, setSenderSource] = useState<string>(campaign.sender_config?.source || 'user_sendgrid');
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>(undefined);
  const [scheduleTime, setScheduleTime] = useState<string>('09:00');
  const [sendingPhase, setSendingPhase] = useState<'config' | 'sending'>('config');

  // Reset sendingPhase when dialog opens
  useEffect(() => {
    if (open) {
      setSendingPhase('config');
    }
  }, [open]);

  // Check if this campaign uses a template (any type requires pre-generation)
  const hasTemplate = !!campaign.template_id;
  const isFromQueue = (campaign.recipient_filters as any)?.target === 'from_queue';

  // Check verification status of queue items for AI campaigns
  const { data: verificationStats } = useQuery({
    queryKey: ['campaign-verification-stats', campaign.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_queue')
        .select('verification_status')
        .eq('campaign_id', campaign.id)
        .eq('status', 'pending');
      if (error) throw error;
      const items = data || [];
      return {
        total: items.length,
        passed: items.filter(i => (i as any).verification_status === 'passed').length,
        flagged: items.filter(i => (i as any).verification_status === 'flagged').length,
        unverified: items.filter(i => !(i as any).verification_status).length,
      };
    },
    enabled: open && hasTemplate,
  });

  // Fetch company default email
  useEffect(() => {
    if (!profile?.company_id) return;
    supabase
      .from('companies')
      .select('default_from_email')
      .eq('id', profile.company_id)
      .single()
      .then(({ data }) => setCompanyEmail((data as any)?.default_from_email || null));
  }, [profile?.company_id]);

  // Fetch preview on open
  useEffect(() => {
    if (open) {
      setLoading(true);
      previewMutation.mutateAsync({ campaignId: campaign.id, preview: true })
        .then(data => setPreview(data))
        .catch(() => setPreview(null))
        .finally(() => setLoading(false));
    }
  }, [open, campaign.id]);

  const userEmailLabel = defaultSender?.from_email
    ? `My Verified Email (${defaultSender.from_email})`
    : 'My Verified Email';

  const companyEmailLabel = companyEmail
    ? `Company Default (${companyEmail})`
    : 'Company Default';

  // Polling query for send progress
  const { data: progressData } = useQuery({
    queryKey: ['campaign-send-progress', campaign.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('email_campaigns')
        .select('sent_count, failed_count, total_recipients, status')
        .eq('id', campaign.id)
        .single();
      if (error) throw error;
      return data;
    },
    refetchInterval: 3000,
    enabled: sendingPhase === 'sending',
  });

  const progressSent = progressData?.sent_count ?? 0;
  const progressFailed = progressData?.failed_count ?? 0;
  const progressTotal = progressData?.total_recipients ?? 0;
  const progressProcessed = progressSent + progressFailed;
  const progressPercent = progressTotal > 0 ? Math.round((progressProcessed / progressTotal) * 100) : 0;
  const isSendComplete = progressTotal > 0 && (progressProcessed >= progressTotal || ['completed', 'sent'].includes(progressData?.status || ''));

  const handleSend = async () => {
    let scheduledFor: string | undefined;
    if (sendMode === 'schedule' && scheduleDate) {
      const [hours, minutes] = scheduleTime.split(':').map(Number);
      const dt = new Date(scheduleDate);
      dt.setHours(hours, minutes, 0, 0);
      scheduledFor = dt.toISOString();
    }

    if (sendMode === 'schedule') {
      await sendMutation.mutateAsync({ campaignId: campaign.id, preview: false, senderSource, scheduledFor });
      onOpenChange(false);
    } else {
      // Switch to progress view IMMEDIATELY
      setSendingPhase('sending');
      // Fire mutation in background (don't await)
      sendMutation.mutate({ campaignId: campaign.id, preview: false, senderSource });
    }
  };

  const recipientCount = preview?.recipient_count || 0;
  const isScheduleValid = sendMode === 'now' || (scheduleDate && scheduleTime);

  // For template campaigns: block if no queue items generated, or if items are flagged
  const hasGenerationBlocker = hasTemplate && campaign.total_recipients === 0;
  const hasFlaggedItems = hasTemplate && verificationStats && verificationStats.flagged > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {sendingPhase === 'sending'
              ? (isSendComplete ? 'Campaign Sent' : 'Sending Campaign…')
              : `Send Campaign: ${campaign.name}`}
          </DialogTitle>
          <DialogDescription>
            {sendingPhase === 'sending'
              ? (isSendComplete ? 'All emails have been processed.' : 'Emails are being sent in the background.')
              : 'Review recipients and sender before sending.'}
          </DialogDescription>
        </DialogHeader>

        {sendingPhase === 'sending' ? (
          <div className="space-y-6 py-4 px-1">
            <div className="flex flex-col items-center gap-4">
              {isSendComplete ? (
                <CheckCircle2 className="h-12 w-12 text-primary" />
              ) : (
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              )}
              <div className="text-center space-y-1">
                <p className="text-lg font-semibold">
                  {isSendComplete ? 'Complete!' : `${progressPercent}%`}
                </p>
                <p className="text-sm text-muted-foreground">
                  Sent <strong>{progressSent}</strong> of <strong>{progressTotal}</strong>
                  {progressFailed > 0 && (
                    <span className="text-destructive"> · {progressFailed} failed</span>
                  )}
                </p>
              </div>
            </div>
            <Progress value={progressPercent} className="h-2" />
          </div>
        ) : (
          <div className="space-y-4 py-2 overflow-y-auto flex-1 min-h-0 px-[5px] pb-[28px] mb-0">
            {/* Info alert */}
            <Alert className="border-blue-500/20 bg-blue-500/5">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-sm">
                {isFromQueue
                  ? 'This will send emails to the selected recipient list. Emails are sent in batches and you can monitor progress from the campaign list.'
                  : 'This will queue emails to all matching recipients. Emails are sent in batches and you can monitor progress from the campaign list.'}
              </AlertDescription>
            </Alert>

            {/* Preview stats */}
            {loading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />Calculating recipients…
              </div>
            ) : preview ? (
              <div className="p-3 rounded-lg bg-muted/50 border border-border/60 space-y-1 text-sm">
                <div><span className="text-muted-foreground">Recipients:</span> <strong>{recipientCount}</strong></div>
                {!isFromQueue && <div><span className="text-muted-foreground">Total Potential:</span> {preview.total_potential || 0}</div>}
                {!isFromQueue && <div><span className="text-muted-foreground">Unsubscribed (filtered):</span> {preview.unsubscribed_count || 0}</div>}
                <div><span className="text-muted-foreground">Target:</span> {preview.target_description || (isFromQueue ? 'From Recipient List' : 'All Leads')}</div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Unable to calculate preview. The campaign will still send to matching leads.</p>
            )}

            {/* High recipient warning */}
            {recipientCount > 50 && (
              <Alert className="border-amber-500/20 bg-amber-500/5">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-sm">
                  You're about to send to <strong>{recipientCount}</strong> recipients. Please double-check your targeting and content before proceeding.
                </AlertDescription>
              </Alert>
            )}

            {/* Template: no emails generated */}
            {hasGenerationBlocker && (
              <Alert className="border-destructive/20 bg-destructive/5">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-sm">
                  No emails have been generated yet. Use <strong>"Generate Emails"</strong> from the campaign action menu first.
                </AlertDescription>
              </Alert>
            )}

            {/* Flagged items */}
            {hasFlaggedItems && (
              <Alert className="border-amber-500/20 bg-amber-500/5">
                <ShieldCheck className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-sm">
                  <strong>{verificationStats!.flagged}</strong> email(s) were flagged during verification. Review them in "View Recipients" before sending.
                </AlertDescription>
              </Alert>
            )}

            {/* Sender source selector */}
            <div className="space-y-1.5">
              <Label htmlFor="send-sender-source">Send from</Label>
              <Select value={senderSource} onValueChange={setSenderSource}>
                <SelectTrigger id="send-sender-source">
                  <SelectValue placeholder="Select sender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user_sendgrid">{userEmailLabel}</SelectItem>
                  <SelectItem value="system_sendgrid">{companyEmailLabel}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Send mode toggle */}
            <div className="space-y-3">
              <Label>When to send</Label>
              <RadioGroup value={sendMode} onValueChange={(v) => setSendMode(v as 'now' | 'schedule')} className="flex gap-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="now" id="send-now" />
                  <Label htmlFor="send-now" className="font-normal cursor-pointer">Send Now</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="schedule" id="send-schedule" />
                  <Label htmlFor="send-schedule" className="font-normal cursor-pointer">Schedule for Later</Label>
                </div>
              </RadioGroup>

              {sendMode === 'schedule' && (
                <div className="space-y-3 pl-1">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !scheduleDate && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {scheduleDate ? format(scheduleDate, 'PPP') : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={scheduleDate}
                          onSelect={setScheduleDate}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Time</Label>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <Input
                        type="time"
                        value={scheduleTime}
                        onChange={(e) => setScheduleTime(e.target.value)}
                        className="w-[140px]"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {sendingPhase === 'sending' ? (
            <Button onClick={() => onOpenChange(false)}>
              {isSendComplete ? 'Close' : 'Close (continues in background)'}
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={handleSend} disabled={sendMutation.isPending || !isScheduleValid || hasGenerationBlocker}>
                {sendMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : sendMode === 'schedule' ? (
                  <CalendarIcon className="h-4 w-4 mr-1.5" />
                ) : (
                  <Send className="h-4 w-4 mr-1.5" />
                )}
                {sendMode === 'schedule' ? 'Schedule' : 'Send Now'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CampaignSendDialog;
